import type { EmployeeField, MatchType, SegmentRule } from "#shared/segmentsTypes.ts";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent } from "@/components/ui/card.tsx";
import { Note } from "@/components/ui/note.tsx";
import { Select, SelectContent, SelectItem, SelectLabel, SelectTrigger } from "@/components/ui/select.tsx";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useI18nextInstance } from "@squide/i18next";
import { EmptyState } from "@packages/core-client/collection/EmptyState.tsx";
import { SectionTitle } from "@packages/core-client/components/SectionTitle.tsx";
import { useCallback, useEffect, useId, useMemo, useState, type ReactNode } from "react";
import type { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { segmentsI18NextInstanceKey } from "../../i18n/i18next.ts";
import { RequiredRuleRow } from "./RequiredRuleRow.tsx";
import { SegmentRuleRow } from "./SegmentRuleRow.tsx";
import { type RuleDraft, toRuleDrafts, toSegmentRules } from "./ruleConditions.ts";

const MATCH_TYPES: MatchType[] = ["all", "any"];

function blankDraft(): RuleDraft {
    return { key: crypto.randomUUID(), values: [] };
}

export interface SegmentRulesProps {
    fields: EmployeeField[];
    rules: SegmentRule[];
    hasRequiredRules: boolean;
    hasUnlistedRules: boolean;
    hasUnlistedRequiredRules: boolean;
    lockedRules?: SegmentRule[];
    matchType: MatchType;
    isMatchTypeFixed?: boolean;
    matchTypeFixedReason?: string;
    isReadOnly?: boolean;
    isOwnedByViewer: boolean;
    ownerDisplayName?: string;
    shouldShowErrors?: boolean;
    onMatchTypeChange?: (matchType: MatchType) => void;
    onRulesChange: (rules: SegmentRule[]) => void;
    onValidityChange: (hasIncompleteRule: boolean) => void;
    children?: ReactNode;
    layout?: "page" | "compact";
}

export function SegmentRules({
    fields,
    rules,
    hasRequiredRules,
    hasUnlistedRules,
    hasUnlistedRequiredRules,
    lockedRules,
    matchType,
    isMatchTypeFixed,
    matchTypeFixedReason,
    isReadOnly,
    isOwnedByViewer,
    ownerDisplayName,
    shouldShowErrors,
    onMatchTypeChange,
    onRulesChange,
    onValidityChange,
    children,
    layout = "page"
}: SegmentRulesProps) {
    const i18nextInstance = useI18nextInstance(segmentsI18NextInstanceKey);
    const { t } = useTranslation("segmentRules", { i18n: i18nextInstance });
    const headingId = useId();
    const matchTypeReasonId = useId();

    const fieldTypeById = useMemo(() => new Map(fields.map(field => [field.id, field.type])), [fields]);

    const lockedDrafts = useMemo(() => (lockedRules ? toRuleDrafts(lockedRules, fieldTypeById) : []), [lockedRules, fieldTypeById]);
    const handleLockedRowNoop = useCallback(() => {}, []);

    const [drafts, setDrafts] = useState<RuleDraft[]>(() => {
        const seeded = toRuleDrafts(rules, fieldTypeById);

        return seeded.length > 0 ? seeded : [blankDraft()];
    });

    const publish = useCallback(
        (next: RuleDraft[]) => {
            setDrafts(next);
            onRulesChange(toSegmentRules(next));
        },
        [onRulesChange]
    );

    useEffect(() => {
        onValidityChange(drafts.some(draft => draft.fieldId || draft.condition || draft.values.length > 0) && toSegmentRules(drafts).length !== drafts.length);
    }, [drafts, onValidityChange]);

    const handleDraftChange = useCallback((draft: RuleDraft) => publish(drafts.map(candidate => (candidate.key === draft.key ? draft : candidate))), [drafts, publish]);
    const handleRemove = useCallback((key: string) => publish(drafts.filter(candidate => candidate.key !== key)), [drafts, publish]);
    const handleAdd = useCallback(() => publish([...drafts, blankDraft()]), [drafts, publish]);

    const handleMatchTypeChange = useCallback(
        (key: Key | null) => {
            const next = MATCH_TYPES.find(candidate => candidate === key);

            if (next) {
                onMatchTypeChange?.(next);
            }
        },
        [onMatchTypeChange]
    );

    const isMatchTypeLocked = !!isReadOnly || !!isMatchTypeFixed;
    const matchTypeReason = isMatchTypeLocked ? matchTypeFixedReason : undefined;

    const header = (
        <div className="flex flex-wrap items-center gap-2">
            <SectionTitle id={headingId} className="text-sm font-medium sm:text-sm">
                {t("heading")}
            </SectionTitle>
            <span className="text-muted-fg text-sm">{t("matchLabel")}</span>
            <Select name="match" aria-label={t("matchLabel")} aria-describedby={matchTypeReason ? matchTypeReasonId : undefined} selectedKey={matchType} onSelectionChange={handleMatchTypeChange} isDisabled={isMatchTypeLocked} className="w-fit [--control-bg:var(--color-bg)]">
                <SelectTrigger />
                <SelectContent items={MATCH_TYPES.map(value => ({ id: value }))}>
                    {item => (
                        <SelectItem id={item.id} textValue={t(`match.${item.id}`)}>
                            <SelectLabel>{t(`match.${item.id}`)}</SelectLabel>
                        </SelectItem>
                    )}
                </SelectContent>
            </Select>
            <span className="text-muted-fg text-sm">{t(`matchSuffix.${matchType}`)}</span>
            {matchTypeReason && (
                <span id={matchTypeReasonId} className="text-muted-fg text-sm">
                    {matchTypeReason}
                </span>
            )}
        </div>
    );

    if (fields.length === 0) {
        return (
            <section aria-labelledby={headingId} className="flex flex-col gap-2">
                {header}
                <EmptyState description={t("noUsableFields")} isEmbedded={layout === "compact"} />
            </section>
        );
    }

    const canRemoveRows = !isReadOnly && (drafts.length > 1 || hasRequiredRules || hasUnlistedRequiredRules || lockedDrafts.length > 0);

    const content = (
        <>
            {hasUnlistedRules && (
                <Note intent="warning">
                    <p className="text-sm">{t("unlistedRules")}</p>
                </Note>
            )}

            {(hasRequiredRules || hasUnlistedRequiredRules) && (
                <>
                    <RequiredRuleRow isOwnedByViewer={isOwnedByViewer} ownerDisplayName={ownerDisplayName} canAddRules={!isReadOnly} layout={layout} />
                    {hasUnlistedRequiredRules && <p className="text-muted-fg text-sm">{t("requiredRule.unlisted")}</p>}
                </>
            )}

            {layout === "page" && (
                <div className="text-muted-fg hidden gap-2 text-xs font-medium tracking-wide uppercase sm:flex" aria-hidden="true">
                    <span className="w-48">{t("columns.property")}</span>
                    <span className="w-56">{t("columns.condition")}</span>
                    <span className="flex-1">{t("columns.value")}</span>
                    {canRemoveRows && <span className="w-9" />}
                </div>
            )}

            <ul className="flex flex-col gap-3">
                {lockedDrafts.map(draft => (
                    <SegmentRuleRow key={draft.key} draft={draft} fields={fields} isReadOnly isLocked canRemove={false} onChange={handleLockedRowNoop} onRemove={handleLockedRowNoop} />
                ))}
                {drafts.map(draft => (
                    <SegmentRuleRow key={draft.key} draft={draft} fields={fields} isReadOnly={isReadOnly} shouldShowErrors={shouldShowErrors} canRemove={canRemoveRows} onChange={handleDraftChange} onRemove={handleRemove} />
                ))}
            </ul>

            {!isReadOnly && (
                <Button type="button" intent="plain" size="sm" className="text-primary self-start px-0 [--btn-icon:var(--color-primary)]" onPress={handleAdd}>
                    <PlusIcon aria-hidden="true" />
                    {t("addRule")}
                </Button>
            )}
        </>
    );

    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-2">
            {header}

            {layout === "page" ? (
                <Card className="overflow-hidden">
                    <CardContent className="flex flex-col gap-3">{content}</CardContent>
                    {children}
                </Card>
            ) : (
                <div className="flex flex-col gap-3">
                    {content}
                    {children}
                </div>
            )}
        </section>
    );
}
